import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import api from '../api/ApiClient';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function Divider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>or continue with</span>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

const providerBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  width: '100%', padding: '10px 16px', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', fontSize: 14, fontWeight: 500, fontFamily: 'inherit',
  cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
};

const hoverOn = e => { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.borderColor = '#94A3B8'; };
const hoverOff = e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border)'; };

function GoogleButton({ onError }) {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const login = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async ({ access_token }) => {
      try {
        setLoading(true);
        await loginWithGoogle(null, access_token);
        navigate('/dashboard');
      } catch (err) {
        onError?.(err?.response?.data?.message || 'Google sign-in failed');
      } finally {
        setLoading(false);
      }
    },
    onError: () => onError?.('Google sign-in failed'),
  });

  return (
    <button
      type="button"
      onClick={() => login()}
      disabled={loading}
      style={{ ...providerBtnStyle, opacity: loading ? 0.6 : 1 }}
      onMouseOver={hoverOn}
      onMouseOut={hoverOff}
    >
      <GoogleIcon />
      {loading ? 'Signing in…' : 'Continue with Google'}
    </button>
  );
}

export default function OAuthButtons({ onError }) {
  const [ghLoading, setGhLoading] = useState(false);

  const handleGitHub = () => {
    setGhLoading(true);
    window.location.href = api.githubAuthUrl;
  };

  return (
    <div>
      <Divider />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* Google */}
        {GOOGLE_CLIENT_ID && <GoogleButton onError={onError} />}

        {/* GitHub — always shown */}
        <button
          type="button"
          onClick={handleGitHub}
          disabled={ghLoading}
          style={{ ...providerBtnStyle, opacity: ghLoading ? 0.6 : 1 }}
          onMouseOver={e => { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.borderColor = '#94A3B8'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
        >
          <GitHubIcon />
          {ghLoading ? 'Redirecting…' : 'Continue with GitHub'}
        </button>

      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
    </svg>
  );
}
